/**
 * Intercepts RTK Query cache to reorder images ONLY for Product Cards/Archives.
 * Order: Product Main (Index 0) -> Variation Main (Index 1) (Only if variation has a custom image)
 */
export const initProductGalleryReorder = (): void => {
  const EcommerceCore = (window as any).EcommerceCore;

  // Guard clause: Ensure the core API is available
  if (
    !EcommerceCore ||
    !EcommerceCore.store ||
    !EcommerceCore.app?.api?.baseApi
  ) {
    return;
  }

  // ======================================================================
  // 1. FETCH INTERCEPTOR (The Secret Sauce for inherited images)
  // We intercept the raw API response to remember the TRUE Parent Main Image ID
  // before the core plugin transforms and mixes the arrays.
  // ======================================================================
  if (!(window as any).__themeProductImageMap) {
    (window as any).__themeProductImageMap = new Map<number, number>();
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const url =
        typeof args[0] === "string"
          ? args[0]
          : args[0] instanceof Request
            ? args[0].url
            : "";

      if (url.includes("/wc/store/v1/products")) {
        try {
          const clone = response.clone();
          const data = await clone.json();
          const map = (window as any).__themeProductImageMap as Map<
            number,
            number
          >;

          // Save the original Parent Main Image ID for every product in the list
          if (Array.isArray(data)) {
            data.forEach((p) => {
              if (p.id && p.images && p.images.length > 0) {
                map.set(p.id, p.images[0].id);
              }
            });
          }
        } catch (e) {
          // Silently ignore parsing errors
        }
      }
      return response;
    };
  }

  const store = EcommerceCore.store;
  const baseApi = EcommerceCore.app.api.baseApi;

  store.subscribe(() => {
    const state = store.getState();
    const queries = state.api.queries;

    for (const [queryKey, queryState] of Object.entries(queries)) {
      if (queryState?.status !== "fulfilled" || !queryState.data) continue;

      // ======================================================================
      // 2. PRODUCT CARDS & ARCHIVES LOGIC ONLY
      // ======================================================================
      if (
        queryKey.startsWith("getProducts(") ||
        queryKey.startsWith("getProductsByIds(")
      ) {
        const isGetProducts = queryKey.startsWith("getProducts(");
        const productsArray = isGetProducts
          ? (queryState.data as any).products
          : (queryState.data as any[]);

        if (!productsArray || productsArray.length === 0) continue;

        // If we already reordered this specific cached data, skip it
        if (productsArray[0].__cardImagesReordered) continue;

        const endpointName = isGetProducts ? "getProducts" : "getProductsByIds";
        const args = (queryState as any).originalArgs;

        store.dispatch(
          baseApi.util.updateQueryData(
            endpointName as any,
            args,
            (draftData: any) => {
              const draftProducts = isGetProducts
                ? draftData.products
                : draftData;

              draftProducts.forEach((product: any) => {
                const map = (window as any).__themeProductImageMap as Map<
                  number,
                  number
                >;
                const originalMainImageId = map.get(product.id);

                if (
                  product.isVariable &&
                  product.hasDefaultVariation &&
                  product.images &&
                  product.images.length > 1
                ) {
                  // ONLY swap if the variation image is a TRULY custom image
                  // (i.e., its ID does not match the Parent's Main Image ID we intercepted)
                  if (
                    originalMainImageId &&
                    product.images[0].id !== originalMainImageId
                  ) {
                    const temp = product.images[0];
                    product.images[0] = product.images[1];
                    product.images[1] = temp;
                  }
                }

                // Tag the product so we don't process it again in infinite loop
                product.__cardImagesReordered = true;
              });
            },
          ),
        );
      }
    }
  });
};
